import { useState, useEffect } from 'react';
import { getPaymentLink, checkPaymentStatus } from '../services/formService';
import { Logger } from '../utils/logger';

interface UsePaymentHandlingProps {
  formUniqueCode?: string;
  formId: string;
  activeField: number;
  formData: any;
  newFormData: any;
  setActiveField: (field: number) => void;
  setBackStep: (step: any) => void;
  backStep: number[];
  setFormData: (data: any) => void;
  setNewFormData: (data: any) => void;
  onNavigateNext: () => void;
}

interface UsePaymentHandlingReturn {
  isPageLoad: boolean;
  paymentDialog: boolean;
  paymentStatus: string;
  generatePaymentLink: (fieldName: string, amount: number) => Promise<void>;
  checkPayment: (link: string) => Promise<void>;
  setPaymentDialog: (open: boolean) => void;
  handleFormObject: (key: string, value: any) => void;
}

/**
 * Custom hook for handling Razorpay payment integration
 *
 * Features:
 * - Generates payment links via Razorpay API
 * - Checks payment status
 * - Handles payment URL parameter recovery from localStorage
 * - Manages payment dialog state
 */
export const usePaymentHandling = ({
  formUniqueCode,
  formId,
  activeField,
  formData,
  newFormData,
  setActiveField,
  setBackStep,
  backStep,
  setFormData,
  setNewFormData,
  onNavigateNext
}: UsePaymentHandlingProps): UsePaymentHandlingReturn => {
  const [isPageLoad, setIsPageLoad] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");

  /**
   * Generates a Razorpay payment link for the specified amount
   */
  const generatePaymentLink = async (fieldName: string, amount: number) => {
    try {
      setIsPageLoad(true);
      const data = {
        customFormUniqueCode: formUniqueCode,
        amount: +amount * 100, // Convert to paise
      };
      const resp = await getPaymentLink(data);
      if (resp && resp.status) {
        Logger.log("Payment link generated:", resp.data.data.paymentLink);
        setIsPageLoad(false);
        handleFormObject(fieldName, resp.data.data.paymentLink);
        // Open payment link in new tab
        window.open(resp.data.data.paymentLink, "_blank");
      }
    } catch (err) {
      setIsPageLoad(false);
      Logger.log("Payment link generation failed:", err);
    }
  };

  /**
   * Checks the payment status and navigates if successful
   */
  const checkPayment = async (link: string) => {
    try {
      setIsPageLoad(true);
      const resp = await checkPaymentStatus(link);
      if (resp && resp.status) {
        setIsPageLoad(false);
        Logger.log("Payment status:", resp.data.data.status);
        if (resp.data.data.status === "captured") {
          onNavigateNext();
          setPaymentDialog(false);
        }
      }
    } catch (err) {
      setIsPageLoad(false);
      Logger.log("Payment status check failed:", err);
    }
  };

  /**
   * Handles form object updates (used for storing payment link)
   */
  const handleFormObject = (key: string, value: any) => {
    const updatedFormData = { ...newFormData };
    updatedFormData[key] = value;
    // Note: Form data update should be handled by parent component
    // This is just for payment-specific logic
  };

  /**
   * Recovery logic: Check URL params for payment status on mount
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatusParam = params.get("razorpay_payment_link_status");
    const paymentId = params.get("razorpay_payment_id");

    const cNewFormDataStr = localStorage.getItem("custom_new_form_data");
    const cFormDataStr = localStorage.getItem("custom_form_data");
    const active = localStorage.getItem("custom_form_activefield");
    const cFormBackStepStr = localStorage.getItem("custom_form_backStep");

    if (
      paymentStatusParam &&
      paymentId &&
      paymentStatusParam.toUpperCase() === "PAID" &&
      active &&
      cFormDataStr &&
      cNewFormDataStr
    ) {
      const cNewFormData = JSON.parse(cNewFormDataStr) || {};
      const cFormData = JSON.parse(cFormDataStr) || {};
      const cFormBackStep = JSON.parse(cFormBackStepStr || "[]");

      if (
        cFormData?.form &&
        cFormData?.form[+active - 1] &&
        cFormData?.form[+active - 1].fields &&
        cFormData?.form[+active - 1].fields[0] &&
        cFormData?.form[+active - 1].fields[0].amount
      ) {
        setActiveField(+active);
        setBackStep(cFormBackStep);
        setFormData(cFormData);
        setNewFormData(cNewFormData);

        setPaymentDialog(true);
        setPaymentStatus("PAID");

        // Auto-close dialog and proceed after 3 seconds
        setTimeout(() => {
          setPaymentDialog(false);
          setIsPageLoad(false);
          setActiveField(+active + 1);
          checkPayment(cNewFormData?.Payment_Amount);
        }, 3000);
      }
    }
  }, [formId]);

  return {
    isPageLoad,
    paymentDialog,
    paymentStatus,
    generatePaymentLink,
    checkPayment,
    setPaymentDialog,
    handleFormObject
  };
};
